import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { match, Pattern } from 'ts-pattern';

import type { UserLanguage } from '~/api/user/types';

import { FetchError, HttpBusinessConflictError, HttpError } from '~/api/errors';
import { useCreateInvitation } from '~/api/invitations';
import { useUser } from '~/api/user';
import { Button, Dialog, ErrorMessage, ExternalLink } from '~/components';
import { Field, Form, Input, Select } from '~/components/Form';
import { EMAIL } from '~/constants/regex';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import { formStyle } from './SendInvitationDialog.css';

type SendInvitationFormType = {
  emailAddress: string;
  firstName: string;
  language: UserLanguage;
  lastName: string;
};

type Props = {
  onClose: () => void;
};

export const SendInvitationDialog = ({ onClose }: Props) => {
  const mutation = useCreateInvitation();
  const { data: user } = useUser();

  const { t } = useTranslation(['common', 'invitations', 'validation']);

  const { control, handleSubmit, setError } = useForm<SendInvitationFormType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      language: user.lang,
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data, {
      onSuccess: (invitation) => {
        toast.success({ description: t('invitations:sendInvitationDialog.success', { name: invitation.name }) });
        onClose();
      },
      onError: (error) => {
        if (error instanceof HttpBusinessConflictError && error.errorCode === 'ExistingAccountCannotBeInvited') {
          setError('emailAddress', { type: 'accountExists', message: t('invitations:sendInvitationDialog.errors.accountAlreadyExists') });
        }

        if (error instanceof HttpBusinessConflictError && error.errorCode === 'ExistingUserCannotBeInvited') {
          setError('emailAddress', { type: 'userExists', message: t('invitations:sendInvitationDialog.errors.userAlreadyExists') });
        }
      },
    });
  });

  const languageItems = (['nl', 'fr'] satisfies UserLanguage[]).map((language) => ({
    label: t(`common:languages.${language}`),
    value: language,
  }));

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose()} open>
      <Dialog.Body>
        <Dialog.Header title={t('invitations:sendInvitationDialog.title')} />

        <Form className={formStyle} onSubmit={onSubmit}>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label>{t('invitations:sendInvitationDialog.form.inviteeFirstNameField.label')}</Field.Label>

                <Input autoFocus onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
              maxLength: {
                value: 255,
                message: t('validation:maxLength', {
                  attribute: t('invitations:sendInvitationDialog.form.inviteeFirstNameField.label'),
                  max: 255,
                }),
              },
            }}
          />

          <Controller
            control={control}
            name="lastName"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label>{t('invitations:sendInvitationDialog.form.inviteeLastNameField.label')}</Field.Label>

                <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
              maxLength: {
                value: 255,
                message: t('validation:maxLength', {
                  attribute: t('invitations:sendInvitationDialog.form.inviteeLastNameField.label'),
                  max: 255,
                }),
              },
            }}
          />

          <Controller
            control={control}
            name="emailAddress"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label>{t('invitations:sendInvitationDialog.form.inviteeEmailAddressField.label')}</Field.Label>

                <Input onBlur={onBlur} onValueChange={onChange} ref={ref} type="email" value={value} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              maxLength: {
                value: 255,
                message: t('validation:maxLength', {
                  attribute: t('invitations:sendInvitationDialog.form.inviteeEmailAddressField.label'),
                  max: 255,
                }),
              },
              pattern: { value: EMAIL, message: t('validation:email.invalid') },
              validate: {
                notBlank: (value) => isNotBlank(value) || t('validation:required'),
                notSelf: (email) => email !== user.email || t('invitations:sendInvitationDialog.errors.noSelfInvite'),
                unique: (email) =>
                  !user.invitations.some((invitation) => email === invitation.email) ||
                  t('invitations:sendInvitationDialog.errors.noDoubleInvite'),
              },
            }}
          />

          <Controller
            control={control}
            name="language"
            render={({ field: { name, onChange, value } }) => (
              <Field.Root name={name}>
                <Field.Label>{t('invitations:sendInvitationDialog.form.inviteeLanguageField.label')}</Field.Label>

                <Select.Root items={languageItems} onValueChange={onChange} value={value}>
                  <Select.Button />

                  <Select.List>
                    {languageItems.map((item) => (
                      <Select.Item key={item.value} value={item.value}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Root>
              </Field.Root>
            )}
            rules={{ required: true }}
          />

          {mutation.isError &&
            match(mutation.error)
              .with(
                Pattern.instanceOf(HttpBusinessConflictError),
                (error) => error.errorCode === 'ExistingAccountCannotBeInvited' || error.errorCode === 'ExistingUserCannotBeInvited',
                () => null,
              )
              .with(Pattern.instanceOf(HttpError), () => (
                <ErrorMessage>
                  <Trans components={{ email: <ExternalLink /> }} i18nKey="common:errors.httpErrorWithLink" />
                </ErrorMessage>
              ))
              .with(Pattern.instanceOf(FetchError), () => <ErrorMessage>{t('common:errors.fetchError')}</ErrorMessage>)
              .otherwise(() => <ErrorMessage>{t('common:errors.unknown')}</ErrorMessage>)}

          <Dialog.Actions>
            <Button icon="Send" loading={mutation.isPending} type="submit">
              {t('invitations:sendInvitationDialog.form.submitButton')}
            </Button>
          </Dialog.Actions>
        </Form>
      </Dialog.Body>
    </Dialog.Root>
  );
};
